import React, { useState, useEffect } from 'react';
import { invoiceAPI } from '../utils/api';
import { numberToWords } from '../utils/numberToWords';
import LineItemsTable from './LineItemsTable';

const COMPANY_DETAILS = {
  name: 'BLUE STAR LAUNCH',
  subtitle: 'CASH INVOICE',
  address: 'Sh. Funadhoo / Maldives',
  phone: '9945469',
  bankAccount: '7724349274001(USD)',
  accountHolder: 'Ibrahim Moosa',
  paymentTerms: 'Make all Payments within 10 days.',
};

function InvoiceForm() {
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    poNumber: '',
    billTo: '',
  });

  const [lineItems, setLineItems] = useState([
    {
      id: 1,
      serialNumber: 1,
      details: '',
      rate: 0,
      taxRate: 0,
      taxAmount: 0,
      total: 0,
    },
  ]);

  const [calculations, setCalculations] = useState({
    subTotal: 0,
    taxTotal: 0,
    grandTotal: 0,
    totalInWords: 'Zero Dollars Only',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Calculate totals whenever line items change
  useEffect(() => {
    const subTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.rate) || 0), 0);
    const taxTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.taxAmount) || 0), 0);
    const grandTotal = subTotal + taxTotal;
    const totalInWords = numberToWords(grandTotal);

    setCalculations({
      subTotal,
      taxTotal,
      grandTotal,
      totalInWords,
    });
  }, [lineItems]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleLineItemChange = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          
          // Recalculate tax amount and total
          if (field === 'rate' || field === 'taxRate') {
            const rate = parseFloat(updated.rate) || 0;
            const taxRate = parseFloat(updated.taxRate) || 0;
            updated.taxAmount = (rate * taxRate) / 100;
            updated.total = rate + updated.taxAmount;
          }
          
          return updated;
        }
        return item;
      })
    );
  };

  const addLineItem = () => {
    const newSerialNumber = lineItems.length + 1;
    setLineItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        serialNumber: newSerialNumber,
        details: '',
        rate: 0,
        taxRate: 0,
        taxAmount: 0,
        total: 0,
      },
    ]);
  };

  const removeLineItem = (id) => {
    if (lineItems.length > 1) {
      const updated = lineItems.filter((item) => item.id !== id);
      // Reassign serial numbers
      const renumbered = updated.map((item, index) => ({
        ...item,
        serialNumber: index + 1,
      }));
      setLineItems(renumbered);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.billTo.trim()) {
      newErrors.billTo = 'Bill To is required';
    }

    if (lineItems.length === 0 || lineItems.every((item) => !item.details.trim())) {
      newErrors.lineItems = 'At least one line item with details is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGeneratePDF = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      // Prepare invoice data
      const invoiceData = {
        invoiceNumber: formData.invoiceNumber,
        date: formData.date,
        poNumber: formData.poNumber,
        billTo: formData.billTo,
        lineItems: lineItems.map(({ id, ...item }) => item),
        subTotal: calculations.subTotal,
        taxTotal: calculations.taxTotal,
        grandTotal: calculations.grandTotal,
        totalInWords: calculations.totalInWords,
      };

      // Generate PDF directly (no database storage)
      const pdfResponse = await invoiceAPI.generatePDF(invoiceData);
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${formData.invoiceNumber}_${formData.date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      if (error.response?.data?.error) {
        setErrors({ submit: error.response.data.error });
      } else {
        setErrors({ submit: 'Failed to generate invoice. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    if (window.confirm('Are you sure you want to clear the form? All data will be lost.')) {
      setFormData({
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        poNumber: '',
        billTo: '',
      });
      setLineItems([
        {
          id: 1,
          serialNumber: 1,
          details: '',
          rate: 0,
          taxRate: 0,
          taxAmount: 0,
          total: 0,
        },
      ]);
      setErrors({});
      setSuccess(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.invoiceNumber.trim() &&
      formData.date &&
      formData.billTo.trim() &&
      lineItems.some((item) => item.details.trim() && item.rate > 0)
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="border-b-2 border-bluestar-700 pb-4 mb-6">
          <h1 className="text-3xl font-bold text-blue-700">{COMPANY_DETAILS.name}</h1>
          <p className="text-gray-600 mt-1">{COMPANY_DETAILS.address}</p>
          <p className="text-gray-600">Phone: {COMPANY_DETAILS.phone}</p>
          <h2 className="text-xl font-semibold text-blue-600 mt-2">{COMPANY_DETAILS.subtitle}</h2>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleInputChange}
              placeholder="BLU-25-XX"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-bluestar-500 ${
                errors.invoiceNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.invoiceNumber && (
              <p className="text-red-500 text-xs mt-1">{errors.invoiceNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-bluestar-500 ${
                errors.date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
            <input
              type="text"
              name="poNumber"
              value={formData.poNumber}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bluestar-500"
            />
          </div>
        </div>

        {/* Bill To */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bill To <span className="text-red-500">*</span>
          </label>
          <textarea
            name="billTo"
            value={formData.billTo}
            onChange={handleInputChange}
            placeholder="Customer name, address, atoll"
            rows="3"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-bluestar-500 ${
              errors.billTo ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.billTo && <p className="text-red-500 text-xs mt-1">{errors.billTo}</p>}
        </div>

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-gray-800">Line Items</h3>
            <button
              onClick={addLineItem}
              className="px-4 py-2 bg-bluestar-600 text-white rounded-md hover:bg-bluestar-700 focus:outline-none focus:ring-2 focus:ring-bluestar-500"
            >
              Add Row
            </button>
          </div>
          {errors.lineItems && (
            <p className="text-red-500 text-xs mb-2">{errors.lineItems}</p>
          )}
          <LineItemsTable
            lineItems={lineItems}
            onItemChange={handleLineItemChange}
            onRemoveItem={removeLineItem}
            formatCurrency={formatCurrency}
          />
        </div>

        {/* Calculations Summary */}
        <div className="bg-gray-50 p-4 rounded-md mb-6">
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Sub Total:</span>
                <span className="font-semibold">{formatCurrency(calculations.subTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Tax:</span>
                <span className="font-semibold">{formatCurrency(calculations.taxTotal)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-bold text-lg">Total (USD):</span>
                <span className="font-bold text-lg">{formatCurrency(calculations.grandTotal)}</span>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-gray-700 font-semibold">Total: </span>
            <span className="text-gray-800">{calculations.totalInWords}</span>
          </div>
        </div>

        {/* Payment Terms and Bank Details */}
        <div className="bg-gray-50 p-4 rounded-md mb-6">
          <p className="text-sm text-gray-700 mb-2">{COMPANY_DETAILS.paymentTerms}</p>
          <p className="text-sm text-gray-700">
            Account Number: <span className="font-semibold">{COMPANY_DETAILS.bankAccount}</span>
          </p>
          <p className="text-sm text-gray-700">
            Account Holder: <span className="font-semibold">{COMPANY_DETAILS.accountHolder}</span>
          </p>
        </div>

        {/* Signature Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border-2 border-gray-300 p-4 rounded-md">
            <h4 className="font-semibold text-gray-800 mb-3">Payment Received</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Date:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Signature:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
            </div>
          </div>
          <div className="border-2 border-gray-300 p-4 rounded-md">
            <h4 className="font-semibold text-gray-800 mb-3">Service Received</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Date:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Signature:</label>
                <div className="h-8 border-b border-gray-300"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <button
            onClick={handleClearForm}
            className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Clear Form
          </button>
          <div className="flex items-center space-x-4">
            {errors.submit && (
              <p className="text-red-500 text-sm">{errors.submit}</p>
            )}
            {success && (
              <p className="text-green-500 text-sm">Invoice generated successfully!</p>
            )}
            <button
              onClick={handleGeneratePDF}
              disabled={!isFormValid() || loading}
              className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-bluestar-500 ${
                isFormValid() && !loading
                  ? 'bg-bluestar-600 text-white hover:bg-bluestar-700'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              {loading ? 'Generating...' : 'Generate PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceForm;

